/// <amd-module name="Search/Shared/Base/Stores/SearchStore" />
import * as VSSStore from "VSS/Reflux/Store";
import * as SearchShared from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";

import {
    ISearchStartedPayload,
    IResultsLoadedPayload,
    ISearchFailedPayload,
    IPageInitializationStartedPayload,
    IRepoStatusRefresh
} from "Search/Shared/Base/ActionsHub";
import { SearchSourceType } from "Search/Shared/Base/Sources/SearchSource";

export enum SearchStatus {
    Loading,

    Success,

    Failed
}

export interface ISearchStoreState<
    TQuery extends SearchShared.EntitySearchQuery,
    TResponse extends SearchShared.EntitySearchResponse
    > {
    query: TQuery;

    response: TResponse | undefined;

    searchStatus: SearchStatus;

    activityId: string;

    source: SearchSourceType;

    fetchMoreScenario?: boolean;

    error?: any;

    LastProcessedTime?: Date;

    Commit?: Date;

    CommitId?: string
}

export class SearchStore<
    TQuery extends SearchShared.EntitySearchQuery,
    TResponse extends SearchShared.EntitySearchResponse,
    TItem
    > extends VSSStore.Store {
    protected _state = {} as ISearchStoreState<TQuery, TResponse>;

    public get state(): ISearchStoreState<TQuery, TResponse> {
        return this._state;
    }

    public startSearch = (payload: ISearchStartedPayload<TQuery>): void => {
        this._state.query = payload.request;
        this._state.searchStatus = SearchStatus.Loading;
        this._state.error = undefined;
        this._state.fetchMoreScenario = payload.fetchMoreScenario;
        this._state.LastProcessedTime = undefined;
        this._state.Commit = undefined;
        this._state.CommitId = undefined;
        this.emitChanged();
    };

    public loadSearchResults = (payload: IResultsLoadedPayload<TResponse, TItem>): void => {
        const { response, activityId, source } = payload;
        if (this.areCorrectResultsLoaded(this._state.query, response)) {
            this._state.response = this.getUpdatedResponse(response);
            this._state.searchStatus = SearchStatus.Success;
            this._state.activityId = activityId;
            this._state.source = source;
            this.emitChanged();
        }
    };

    public failSearch = (payload: ISearchFailedPayload<TQuery>): void => {
        const { query, activityId, error } = payload;
        if (this.areQueriesSame(this._state.query, query)) {
            this._state.searchStatus = SearchStatus.Failed;
            this._state.error = error;
            this._state.response = undefined;
            this._state.fetchMoreScenario = undefined;
            this._state.activityId = activityId;
            this.emitChanged();
        }
    };

    /**
    *   Update only query state as we need adequate searchstorestate on search landing page
    */
    public updateQuery = (payload: IPageInitializationStartedPayload<TQuery>): void => {
        this._state.query = payload.request;
        this.emitChanged();
    };

    /**
     * For now going with a simple approach of compare the query in response and state.
     * Once the query in the response is remove, owing to new contracts, we'll use queryId(guid) to determine
     * the freshness of the results.
     * @param query
     * @param response
     */
    protected areCorrectResultsLoaded = (query: TQuery, response: TResponse): boolean => {
        return true;
    }

    /**
     * Return the results after comparing the queries.
     * @param queryInState
     * @param queryInPayload
     */
    protected areQueriesSame = (queryInState: TQuery, queryInPayload: TQuery): boolean => {
        return true;
    }

    /**
     * Return the response either by appending the results to current set of results
     * or just returning the results based on whether its fetchMoreScenario or not.
     * @param queryInState
     * @param queryInPayload
     */
    protected getUpdatedResponse = (responseInPayload: TResponse): TResponse => {
        return responseInPayload;
    }

    public updateRefreshInfo = (responseInPayload: IRepoStatusRefresh): void => {
        this._state.LastProcessedTime = responseInPayload.lastIndexedTime;
        this._state.Commit = responseInPayload.commit;
        this._state.CommitId = responseInPayload.commitId;
        this.emitChanged();
    }

}
